import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  PreferenceDto,
  PreferenceListDto,
  UpsertPreferenceBodyDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';

export const preferenceKeys = {
  pinnedOrganizations: 'extension-pinned-organizations',
  tableSettings: 'table-settings',
};

export enum SpendBrainTableName {
  Documents = 'documents',
  VirtualDocuments = 'virtual-documents',
  SibLocations = 'sib-locations',
  VectorLocations = 'vector-locations',
  Tickets = 'tickets',
  Contracts = 'contracts',
  InvoiceDetails = 'invoice-details',
  SubsidiaryOrganizations = 'subsidiary-organizations',
  BillingSummary = 'billing-summary',
  Savings = 'savings',
  Vendors = 'vendors',
}

export enum SidecarTableName {
  DashboardUpcomingTicketsWidget = 'dashboard-upcoming-tickets-widget',
  DashboardOpenTasksWidget = 'dashboard-open-tasks-widget',
  DashboardPinnedOrganizations = 'dashboard-pinned-organizations',
  DashboardPinnedVendors = 'dashboard-pinned-vendors',
  DashboardPinnedProjects = 'dashboard-pinned-projects',
  DashboardPinnedTeams = 'dashboard-pinned-teams',
  DashboardPinnedAudits = 'dashboard-pinned-audits',
  Organizations = 'organizations',
  OrganizationSubsidiaryOrganizations = 'organization-subsidiary-organizations',
  OrganizationLocations = 'organization-locations',
  OrganizationActionItems = 'organization-action-items',
  OrganizationTasks = 'organization-tasks',
  OrganizationTickets = 'organization-tickets',
  OrganizationLocationsImportDuplicatesCompact = 'organization-locations-import-duplicates-compact',
  OrganizationSpendEstimated = 'organization-spend-estimated',
  OrganizationDocuments = 'organization-documents',
  OrganizationInvoices = 'organization-invoices',
  OrganizationVendors = 'organization-vendors',
  OrganizationVendorAccounts = 'organization-vendor-accounts',
  OrganizationOnlineAccess = 'organization-online-access',
  OrganizationAddVendors = 'organization-add-vendors',
  OrganizationAudits = 'organization-audits',
  OrganizationSibContractAudits = 'organization-sib-contract-audits',
  OrganizationSibContracts = 'organization-sib-contracts',
  OrganizationServices = 'organization-services',
  AuditFormSibContracts = 'audit-form-sib-contracts',
  AuditFormVendors = 'audit-form-vendors',
  AuditAttachedDocuments = 'audit-attached-documents',
  AuditDocumentsRepository = 'audit-documents-repository',
  DataEntryDocumentsRepository = 'data-entry-documents-repository',
  AuditVendorAccountsRepository = 'audit-vendor-accounts-repository',
  AuditNegotiationsSpendItems = 'audit-negotiations-spend-items',
  AuditNegotiationsClientSpendItems = 'audit-negotiations-client-spend-items',
  AuditInitialValidationSpendItems = 'audit-initial-validation-spend-items',
  AuditMonitoringSavingsSpendItems = 'audit-monitoring-savings-spend-items',
  AuditInvoiceLocationsRepository = 'audit-invoice-locations-repository',
  Vendors = 'vendors',
  VendorContacts = 'vendor-contacts',
  VendorTickets = 'vendor-tickets',
  VendorContracts = 'vendor-contracts',
  ProjectTasks = 'project-tasks',
  ProjectFiles = 'project-files',
  ProjectTickets = 'project-tickets',
  VendorTasks = 'vendor-tasks',
  VendorsInvalidImport = 'vendors-invalid-import',
  SpendEstimatedInvalidImport = 'spend-estimated-invalid-import',
  WorkTasks = 'work-tasks',
  WorkTickets = 'work-tickets',
  WorkActionItems = 'work-action-items',
  AuditNegotiationsBulkApprovePreview = 'audit-negotiations-bulk-approve-preview',
  AuditBackfillSpendItems = 'audit-backfill-spend-items',
  WorkRecurringTasks = 'work-recurring-tasks',
  OrganizationRecurringTasks = 'organization-recurring-tasks',
  OrganizationVsrReports = 'organization-vsr-reports',
  OrganizationVsrReportSpendLineItems = 'organization-vsr-report-spend-line-items',
}

export interface TableColumnPreference {
  enabled?: boolean;
  visible?: boolean;
  pinned?: 'left' | 'right' | false;
}

export type TableSettings = Partial<
  Record<SpendBrainTableName, { [key: string]: TableColumnPreference }>
> &
  Partial<Record<SidecarTableName, { [key: string]: TableColumnPreference }>>;

export const useGetPreferences = (
  query: Parameters<
    typeof api.preference.preferenceControllerGetPreferences
  >[0],
  options?: UseQueryOptions<PreferenceListDto, Error, PreferenceListDto>
) =>
  useQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.UserPreferences,
      query?.type,
      query?.organizationId,
      query?.key,
    ],
    queryFn: async () =>
      api.preference.preferenceControllerGetPreferences(query),
  });

interface UpsertPreferencesParams {
  key: string;
  query: Parameters<
    typeof api.preference.preferenceControllerUpsertPreferences
  >[1];
  data: UpsertPreferenceBodyDto;
}

export const useUpdatePreferences = (
  options?: UseMutationOptions<PreferenceDto, Error, UpsertPreferencesParams>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.UserPreferences],
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ key, query, data }) =>
      api.preference.preferenceControllerUpsertPreferences(key, query, data),
  });
};
